import {
  iconsApiRef,
  useApi,
  useTranslationRef,
} from '@backstage/frontend-plugin-api';
import {
  IconBox,
  projectEnvironmentsTranslationRef,
} from '@internal/plugin-waadoo-core';
import type { OdooVersion } from './odooVersions';
import { odooTranslationRef } from './translation';

/**
 * `YYYY-MM-DD` as the reader's short date — « 26/09/2026 » in French. Read
 * and written in UTC: it is a calendar date, not an instant.
 */
export function formatBuildDate(build: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${build}T00:00:00Z`));
}

/**
 * The Odoo version of an environment (`useOdooVersions`): the Odoo logo and
 * « Odoo 19.0 », the series, in a neutral `waadoo-pill`; the build — « Build
 * du 26/09/2026 (19.0-20260926) », the raw version alone without a build
 * date — in a tooltip for the mouse and in visually hidden text for a screen
 * reader, as the ephemeral badge did before its ghost (2026-10-13). Nothing
 * without a version: the badge simply appears once the backend answered.
 *
 * Monochrome like every brand logo of the app (components/ui/icons/brandIcons.tsx): the
 * logo takes the pill's secondary ink, a pair components/ui/tokens.test.ts already
 * holds at 4.5:1 on neutral-2 in both themes (« sidebar branch rows »).
 */
export function OdooVersionBadge(props: { version: OdooVersion | undefined }) {
  const { version } = props;
  const { t } = useTranslationRef(odooTranslationRef);
  // La locale de la page (`format.locale`).
  const { t: tPage } = useTranslationRef(projectEnvironmentsTranslationRef);
  const iconsApi = useApi(iconsApiRef);

  if (!version) {
    return null;
  }
  const description = version.build
    ? t('odooVersion.build', {
        date: formatBuildDate(version.build, tPage('format.locale')),
        raw: version.raw,
      })
    : version.raw;
  const odooIcon = iconsApi.icon('odoo');

  return (
    <span className="waadoo-pill waadoo-odoo-version" title={description}>
      {odooIcon && <IconBox size="inherit">{odooIcon}</IconBox>}
      <span>{t('odooVersion.label', { series: version.series })}</span>
      {/* The comma keeps the two apart in a name computed from the row. */}
      <span className="waadoo-visually-hidden">, {description}</span>
    </span>
  );
}
